// Cevapsız arama takibi.
//
// 1-1 aramalar `callRooms` koleksiyonunda yaşar. Oda `status: 'waiting'` olarak
// açılır ve YALNIZCA aranan taraf katılınca 'active' olur (bkz.
// VideoCallContext.joinCallRoom). Bu yüzden belirli bir süre sonunda hâlâ
// 'waiting' olan bir oda = aranan kişinin açamadığı arama.
//
// Cevapsız arama iki şey üretir:
//   1. Aranan kişinin üst çubuğunda bildirim (odanın kendisi okunur,
//      ayrı koleksiyon tutulmaz).
//   2. Aranan kişiye bilgilendirme e-postası (Cloud Function).

import { doc, runTransaction, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

/** Cevaplanmayan davetin "cevapsız" sayılması için geçmesi gereken süre. */
export const MISSED_CALL_AFTER_MS = 60 * 1000;

/** Üst çubukta gösterilecek cevapsız aramaların geriye dönük sınırı. */
export const MISSED_CALL_HISTORY_MS = 7 * 24 * 60 * 60 * 1000;

const MISSED_CALL_EMAIL_URL =
  'https://us-central1-habcare-8ecad.cloudfunctions.net/sendMissedCallNotification';

export const CALL_TYPE_LABELS = {
  video: 'görüntülü arama',
  voice: 'sesli arama',
};

export const getCallTypeLabel = (callType) =>
  CALL_TYPE_LABELS[callType] || CALL_TYPE_LABELS.video;

/** Firestore Timestamp / ISO / ms → Date (çözülemezse null). */
export const toDate = (value) => {
  if (!value) return null;
  const date = value.toDate ? value.toDate() : new Date(value.seconds ? value.seconds * 1000 : value);
  return Number.isNaN(date?.getTime?.()) ? null : date;
};

/** "14:05" ya da "12 Mart 14:05" — bugünse yalnızca saat gösterilir. */
export const formatMissedCallTime = (value) => {
  const date = toDate(value);
  if (!date) return '';
  const today = new Date();
  const sameDay =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const time = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return time;
  return `${date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })} ${time}`;
};

/** Cevapsız bildirim e-postasını tetikler; başarısızlık akışı bozmaz. */
const sendMissedCallEmail = async (room) => {
  if (!room?.inviteeEmail) return;
  try {
    await fetch(MISSED_CALL_EMAIL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        receiverEmail: room.inviteeEmail,
        receiverName: room.inviteeName || '',
        callerName: room.createdByName || '',
        callerEmail: room.createdBy || '',
        callType: room.callType || room.type || 'video',
        calledAt: new Date().toISOString(),
      }),
    });
  } catch (error) {
    // Mail gönderilemese bile uygulama içi bildirim yerinde kalır
    console.debug('Cevapsız arama maili gönderilemedi:', error);
  }
};

/**
 * Odayı cevapsız olarak işaretler ve bilgilendirme e-postasını tetikler.
 *
 * Hem arayan hem aranan taraf çağırabilir; geçiş bir işlem (transaction)
 * içinde yapıldığı için yalnızca ilk çağrı başarılı olur ve e-posta tek kez
 * gider. Kabul edilmiş, reddedilmiş veya zaten cevapsız işaretlenmiş odalara
 * dokunulmaz.
 *
 * @returns {Promise<boolean>} bu çağrı odayı cevapsıza çevirdiyse true
 */
export const markCallMissed = async (room) => {
  const roomId = room?.roomId || room?.id;
  if (!roomId) return false;

  const roomRef = doc(db, 'callRooms', roomId);
  let missedRoom = null;

  try {
    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(roomRef);
      if (!snapshot.exists()) throw new Error('room-missing');

      const data = snapshot.data();
      if (data.status !== 'waiting') throw new Error('already-handled');

      transaction.update(roomRef, {
        status: 'missed',
        missedAt: serverTimestamp(),
      });
      missedRoom = { ...data, roomId };
    });
  } catch (error) {
    if (error?.message !== 'already-handled' && error?.message !== 'room-missing') {
      console.debug('Cevapsız arama işaretlenemedi:', error);
    }
    return false;
  }

  if (missedRoom) await sendMissedCallEmail(missedRoom);
  return true;
};

/** Cevapsız arama bildirimini okundu işaretler. */
export const markMissedCallSeen = async (roomId) => {
  if (!roomId) return;
  try {
    await updateDoc(doc(db, 'callRooms', roomId), { missedSeenAt: serverTimestamp() });
  } catch (error) {
    console.debug('Cevapsız arama okundu işaretlenemedi:', error);
  }
};
